import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	Archive,
	ArrowLeft,
	Building2,
	ChevronDown,
	ClipboardCheck,
	Clock,
	Download,
	Eye,
	FileText,
	Loader2,
	Pencil,
	Plus,
	Save,
	Scale,
	Send,
	Target,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { trpcClient } from "@/integrations/tanstack-query/root-provider";
import { supabase } from "@/lib/supabase-browser";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { PdfFormEditorV2 } from "@/components/pdf-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";

interface ContractPeriod {
	id: string;
	name: string;
	startDate: string;
	endDate: string;
}

export const Route = createFileRoute("/solicitations/$id/edit")({
	component: EditSolicitationPage,
});

function EditSolicitationPage() {
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const { user } = useAuth();
	const trpc = useTRPC();
	const [activeTab, setActiveTab] = useState("details");
	const [isSaving, setIsSaving] = useState(false);
	const [isPublishing, setIsPublishing] = useState(false);

	// Fetch solicitation data
	const { data: solicitation, isLoading } = useQuery(
		trpc.solicitations.get.queryOptions({ id }),
	);

	// Publish mutation
	const publishMutation = useMutation(
		trpc.solicitations.publish.mutationOptions(),
	);

	// Redirect non-editable solicitations to detail page
	// Draft and open (published/scheduled) can be edited
	useEffect(() => {
		if (solicitation && !["draft", "open"].includes(solicitation.status)) {
			toast.error("Only draft and open solicitations can be edited");
			navigate({ to: "/solicitations/$id", params: { id } });
		}
	}, [solicitation, navigate, id]);

	// Helper to determine if solicitation is published (visible to vendors)
	const isPublished =
		solicitation?.status === "open" &&
		(!solicitation.publish_date ||
			new Date(solicitation.publish_date) <= new Date());

	const [formData, setFormData] = useState({
		title: "",
		description: "",
		type: "",
		publishDate: "",
		publishTime: "",
		dueDate: "",
		dueTime: "",
		qaDeadlineDate: "",
		qaDeadlineTime: "",
		memberOrgId: "",
		pointOfContactId: "",
	});

	// Get user's timezone for display
	const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const timezoneAbbr = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() ?? userTimezone;

	// Helper to convert UTC ISO string to local date and time
	const parseUtcToLocal = (isoString: string | null | undefined) => {
		if (!isoString) return { date: "", time: "" };
		const date = new Date(isoString);
		const localDate = date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
		const localTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }); // HH:MM format
		return { date: localDate, time: localTime };
	};

	// Helper to convert local date and time to UTC ISO string
	const localToUtcIso = (dateStr: string, timeStr: string) => {
		if (!dateStr) return undefined;
		const timeToUse = timeStr || "00:00";
		const localDate = new Date(`${dateStr}T${timeToUse}:00`);
		return localDate.toISOString();
	};

	// Fetch accessible organizations for the dropdown (includes buyer, member, and subtier orgs)
	const { data: accessibleOrgs } = useQuery(
		trpc.organizations.getAccessibleOrgs.queryOptions(),
	);

	// Fetch members for the point of contact dropdown
	// Uses listWithAccess to get members with inherited access from parent orgs
	const selectedOrgId = formData.memberOrgId || user?.organizationId || "";
	const { data: orgMembers } = useQuery(
		trpc.members.listWithAccess.queryOptions({
			organizationId: selectedOrgId,
		}),
	);

	// Use organization evaluation criteria checkbox
	const [useOrgCriteria, setUseOrgCriteria] = useState(true);

	// Fetch organization evaluation criteria with inheritance (always fetch to know if checkbox should be shown)
	const { data: orgEvalCriteria } = useQuery(
		trpc.organizations.listEvaluationCriteriaWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	// Fetch solicitation-specific evaluation criteria
	const { data: solicitationEvalCriteriaData } = useQuery(
		trpc.solicitations.listEvalCriteria.queryOptions({ solicitationId: id }),
	);

	// Mutation for saving solicitation evaluation criteria
	const saveEvalCriteriaMutation = useMutation(
		trpc.solicitations.saveEvalCriteria.mutationOptions(),
	);

	// Store solicitation-specific criteria separately (for when we uncheck the box)
	const [solicitationCriteria, setSolicitationCriteria] = useState<{
		id?: string;
		title: string;
		description: string;
		maxPoints: number;
		weight: number;
		sortOrder: number;
	}[]>([]);

	// Determine if org has criteria (to show/hide checkbox)
	const hasOrgCriteria = orgEvalCriteria && orgEvalCriteria.length > 0;

	// Initialize useOrgCriteria and solicitationCriteria from database
	useEffect(() => {
		if (solicitationEvalCriteriaData) {
			setUseOrgCriteria(solicitationEvalCriteriaData.useOrgCriteria);
			setSolicitationCriteria(
				solicitationEvalCriteriaData.criteria.map((c) => ({
					id: c.id,
					title: c.title,
					description: c.description ?? "",
					maxPoints: c.max_points,
					weight: Number(c.weight),
					sortOrder: c.sort_order,
				})),
			);
		}
	}, [solicitationEvalCriteriaData]);

	// Fetch inherited org requirements (docs, compliance, forms)
	const { data: inheritedRequiredDocs } = useQuery(
		trpc.organizations.listRequiredDocsWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	const { data: inheritedComplianceChecksData } = useQuery(
		trpc.organizations.listComplianceChecksWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	const { data: inheritedOrgForms } = useQuery(
		trpc.organizations.listOrgFormsWithInheritance.queryOptions(
			{ organizationId: selectedOrgId },
			{ enabled: !!selectedOrgId },
		),
	);

	// Update mutation
	const updateSolicitationMutation = useMutation(
		trpc.solicitations.update.mutationOptions(),
	);

	// Update form data when solicitation loads
	useEffect(() => {
		if (solicitation) {
			const publishLocal = parseUtcToLocal(solicitation.publish_date);
			const dueLocal = parseUtcToLocal(solicitation.due_date);
			const qaDeadlineLocal = parseUtcToLocal(solicitation.qa_deadline);
			setFormData({
				title: solicitation.title ?? "",
				description: solicitation.description ?? "",
				type: solicitation.type ?? "",
				publishDate: publishLocal.date,
				publishTime: publishLocal.time,
				dueDate: dueLocal.date,
				dueTime: dueLocal.time,
				qaDeadlineDate: qaDeadlineLocal.date,
				qaDeadlineTime: qaDeadlineLocal.time,
				memberOrgId: solicitation.member_org_id ?? "",
				pointOfContactId: solicitation.point_of_contact_id ?? "",
			});
		}
	}, [solicitation]);

	// Smart codes state - simple free text values
	const [smartCodes, setSmartCodes] = useState<string[]>([]);

	// Fetch existing smart codes
	const { data: existingSmartCodes } = useQuery(
		trpc.smartCodes.list.queryOptions({ solicitationId: id }),
	);

	// Update smart codes mutation
	const updateSmartCodesMutation = useMutation(
		trpc.smartCodes.update.mutationOptions(),
	);

	// Initialize smart codes from fetched data
	useEffect(() => {
		if (existingSmartCodes && existingSmartCodes.length > 0) {
			setSmartCodes(existingSmartCodes.map((sc) => sc.value));
		}
	}, [existingSmartCodes]);

	// Fetch existing solicitation-specific compliance checks (including archived)
	const { data: allComplianceChecks } = useQuery(
		trpc.complianceChecks.list.queryOptions({ solicitationId: id, includeArchived: true }),
	);
	// Memoize filtered arrays to prevent infinite re-render loops
	const existingComplianceChecks = useMemo(
		() => allComplianceChecks?.filter(c => !c.archived_at) ?? [],
		[allComplianceChecks]
	);
	const archivedComplianceChecks = useMemo(
		() => allComplianceChecks?.filter(c => c.archived_at) ?? [],
		[allComplianceChecks]
	);

	// Update compliance checks mutation
	const updateComplianceChecksMutation = useMutation(
		trpc.complianceChecks.update.mutationOptions(),
	);

	// Initialize solicitation compliance checks from fetched data
	useEffect(() => {
		if (existingComplianceChecks && existingComplianceChecks.length > 0) {
			setSolicitationComplianceChecks(
				existingComplianceChecks.map((c) => ({
					id: c.id,
					name: c.title,
					description: c.description ?? "",
					required: c.is_required,
					inherited: false,
				})),
			);
		}
	}, [existingComplianceChecks]);

	// Fetch existing requested documents (including archived)
	const { data: allRequiredDocs } = useQuery(
		trpc.forms.requestedDocuments.list.queryOptions({ solicitationId: id, includeArchived: true }),
	);
	// Memoize filtered arrays to prevent infinite re-render loops
	const existingRequiredDocs = useMemo(
		() => allRequiredDocs?.filter(d => !d.archived_at) ?? [],
		[allRequiredDocs]
	);
	const archivedRequiredDocs = useMemo(
		() => allRequiredDocs?.filter(d => d.archived_at) ?? [],
		[allRequiredDocs]
	);

	// Requested documents mutations
	const createRequiredDocMutation = useMutation(
		trpc.forms.requestedDocuments.create.mutationOptions(),
	);
	const deleteRequiredDocMutation = useMutation(
		trpc.forms.requestedDocuments.delete.mutationOptions(),
	);

	// Initialize required documents from fetched data
	useEffect(() => {
		if (existingRequiredDocs && existingRequiredDocs.length > 0) {
			setRequiredDocuments(
				existingRequiredDocs.map((doc) => ({
					id: doc.id,
					name: doc.title,
					description: doc.description ?? "",
					required: doc.is_required,
					inherited: false,
				})),
			);
		}
	}, [existingRequiredDocs]);

	// Attachments state and queries
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

	// Fetch existing attachments (including archived for "Show Archived" feature)
	const { data: allAttachments, isLoading: attachmentsLoading } = useQuery(
		trpc.attachments.list.queryOptions({ solicitationId: id, includeArchived: true }),
	);
	// Memoize filtered arrays to prevent infinite re-render loops
	const attachments = useMemo(
		() => allAttachments?.filter(a => !a.archived_at) ?? [],
		[allAttachments]
	);
	const archivedAttachments = useMemo(
		() => allAttachments?.filter(a => a.archived_at) ?? [],
		[allAttachments]
	);

	// Attachment mutations
	const createSignedUploadUrlMutation = useMutation(
		trpc.attachments.createSignedUploadUrl.mutationOptions(),
	);
	const uploadAttachmentMutation = useMutation(
		trpc.attachments.upload.mutationOptions(),
	);
	const deleteAttachmentMutation = useMutation(
		trpc.attachments.delete.mutationOptions(),
	);

	const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files || files.length === 0) return;

		setIsUploadingAttachment(true);
		try {
			for (const file of Array.from(files)) {
				// Get signed upload URL
				const { signedUrl, path } =
					await createSignedUploadUrlMutation.mutateAsync({
						solicitationId: id,
						fileName: file.name,
					});

				// Upload file directly to storage
				const uploadResponse = await fetch(signedUrl, {
					method: "PUT",
					headers: {
						"Content-Type": file.type || "application/octet-stream",
					},
					body: file,
				});

				if (!uploadResponse.ok) {
					throw new Error(`Failed to upload ${file.name}`);
				}

				// Record attachment in database
				await uploadAttachmentMutation.mutateAsync({
					solicitationId: id,
					title: file.name,
					filePath: path,
					fileSize: file.size,
					mimeType: file.type || null,
				});
			}

			// Refresh attachments list
			queryClient.invalidateQueries({
				queryKey: trpc.attachments.list.queryKey({ solicitationId: id }),
			});
			toast.success(
				files.length === 1
					? "File uploaded successfully"
					: `${files.length} files uploaded successfully`,
			);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to upload file",
			);
		} finally {
			setIsUploadingAttachment(false);
			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleDeleteAttachment = async (attachmentId: string) => {
		try {
			const result = await deleteAttachmentMutation.mutateAsync({ id: attachmentId });
			queryClient.invalidateQueries({
				queryKey: trpc.attachments.list.queryKey({ solicitationId: id, includeArchived: true }),
			});
			toast.success(result.archived ? "Attachment archived" : "Attachment deleted");
		} catch (error) {
			toast.error("Failed to delete attachment");
		}
	};

	const handleViewAttachment = async (filePath: string) => {
		try {
			const { signedUrl } = await trpcClient.attachments.getDownloadUrl.query({
				filePath,
			});
			window.open(signedUrl, "_blank");
		} catch (error) {
			toast.error("Failed to view file");
		}
	};

	const handleDownloadAttachment = async (filePath: string, title: string) => {
		try {
			const { signedUrl } = await trpcClient.attachments.getDownloadUrl.query({
				filePath,
			});
			// Fetch and download the file
			const response = await fetch(signedUrl);
			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = title || "document";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			toast.error("Failed to download file");
		}
	};

	const isPdfFile = (filePath: string) => filePath?.toLowerCase().endsWith(".pdf");

	// Initialize criteria based on useOrgCriteria flag
	useEffect(() => {
		if (useOrgCriteria && hasOrgCriteria) {
			// Use org criteria with their weights
			setCriteria(
				orgEvalCriteria.map((c, index) => ({
					id: c.id,
					title: c.title,
					description: c.description ?? "",
					maxPoints: c.max_points,
					sortOrder: c.sort_order ?? index + 1,
				})),
			);
			// Set weights from org criteria
			const weights: Record<string, number> = {};
			for (const c of orgEvalCriteria) {
				weights[c.id] = Number(c.weight) || 0;
			}
			setCriteriaWeights(weights);
		} else if (!useOrgCriteria && solicitationCriteria.length > 0) {
			// Use solicitation-specific criteria
			setCriteria(
				solicitationCriteria.map((c, index) => ({
					id: c.id ?? crypto.randomUUID(),
					title: c.title,
					description: c.description ?? "",
					maxPoints: c.maxPoints,
					sortOrder: c.sortOrder ?? index + 1,
				})),
			);
			// Set weights from solicitation criteria
			const weights: Record<string, number> = {};
			for (const c of solicitationCriteria) {
				const criterionId = c.id ?? "";
				if (criterionId) {
					weights[criterionId] = c.weight || 0;
				}
			}
			setCriteriaWeights(weights);
		}
	}, [useOrgCriteria, orgEvalCriteria, hasOrgCriteria, solicitationCriteria]);

	const handleAddSmartCode = () => {
		setSmartCodes([...smartCodes, ""]);
	};

	const handleRemoveSmartCode = (index: number) => {
		if (smartCodes.length > 0) {
			setSmartCodes(smartCodes.filter((_, i) => i !== index));
		}
	};

	const handleSmartCodeChange = (index: number, value: string) => {
		const newCodes = [...smartCodes];
		newCodes[index] = value;
		setSmartCodes(newCodes);
	};

	// Milestones state
	interface Milestone {
		id?: string; // DB id for existing milestones
		title: string;
		date: string;
		time: string;
		isMeeting: boolean;
		isRequired: boolean;
		meetingAddress: string;
		meetingLink: string;
	}
	const [milestones, setMilestones] = useState<Milestone[]>([]);

	// Fetch existing milestones
	const { data: existingMilestones } = useQuery(
		trpc.milestones.list.queryOptions({ solicitationId: id }),
	);

	// Milestones mutations
	const createMilestoneMutation = useMutation(
		trpc.milestones.create.mutationOptions(),
	);
	const updateMilestoneMutation = useMutation(
		trpc.milestones.update.mutationOptions(),
	);
	const deleteMilestoneMutation = useMutation(
		trpc.milestones.delete.mutationOptions(),
	);

	// Initialize milestones from fetched data
	useEffect(() => {
		if (existingMilestones && existingMilestones.length > 0) {
			setMilestones(
				existingMilestones.map((m) => {
					const local = parseUtcToLocal(m.date);
					return {
						id: m.id,
						title: m.title,
						date: local.date,
						time: local.time,
						isMeeting: m.is_meeting,
						isRequired: m.is_mandatory,
						meetingAddress: m.meeting_location ?? "",
						meetingLink: m.meeting_link ?? "",
					};
				}),
			);
		}
	}, [existingMilestones]);

	const addMilestone = () => {
		setMilestones([
			...milestones,
			{
				title: "",
				date: "",
				time: "",
				isMeeting: false,
				isRequired: false,
				meetingAddress: "",
				meetingLink: "",
			},
		]);
	};

	const removeMilestone = (index: number) => {
		setMilestones(milestones.filter((_, i) => i !== index));
	};

	// Contract periods state - initialize empty (optional)
	const [contractPeriods, setContractPeriods] = useState<ContractPeriod[]>([]);

	// Fetch contract periods
	const { data: existingContractPeriods } = useQuery(
		trpc.solicitations.listContractPeriods.queryOptions({ solicitationId: id }),
	);

	// Mutation for saving contract periods
	const saveContractPeriodsMutation = useMutation(
		trpc.solicitations.saveContractPeriods.mutationOptions(),
	);

	// Initialize contract periods from database
	useEffect(() => {
		if (existingContractPeriods && existingContractPeriods.length > 0) {
			setContractPeriods(
				existingContractPeriods.map((p) => ({
					id: p.id,
					name: p.name,
					startDate: p.start_date ?? "",
					endDate: p.end_date ?? "",
				})),
			);
		}
	}, [existingContractPeriods]);

	// Forms state - must be before early returns
	interface SolicitationForm {
		id: string;
		name: string;
		description: string;
		required: boolean;
		formTemplateId: string;
		pdfPath: string | null;
		pdfStoragePath: string | null;
	}
	const [formEditorOpen, setFormEditorOpen] = useState(false);
	const [editingForm, setEditingForm] = useState<SolicitationForm | null>(null);
	const [newFormName, setNewFormName] = useState("");
	const [newFormDescription, setNewFormDescription] = useState("");
	const [newFormRequired, setNewFormRequired] = useState(true);
	const queryClient = useQueryClient();

	// Query solicitation forms from tRPC (including archived)
	const { data: allSolicitationFormsData, isLoading: isLoadingForms } = useQuery(
		trpc.forms.solicitationForms.list.queryOptions(
			{ solicitationId: id, includeArchived: true },
			{ enabled: !!id },
		),
	);
	// Memoize filtered arrays to prevent infinite re-render loops
	const solicitationFormsData = useMemo(
		() => allSolicitationFormsData?.filter(f => !f.archived_at) ?? [],
		[allSolicitationFormsData]
	);
	const archivedSolicitationFormsData = useMemo(
		() => allSolicitationFormsData?.filter(f => f.archived_at) ?? [],
		[allSolicitationFormsData]
	);

	// Mutations for forms
	const createTemplateMutation = useMutation(
		trpc.forms.templates.create.mutationOptions({
			onError: (error) => {
				toast.error(`Failed to create form template: ${error.message}`);
			},
		}),
	);

	const saveWidgetsMutation = useMutation(
		trpc.pdfTemplates.saveWidgets.mutationOptions(),
	);

	const updateTemplateMutation = useMutation(
		trpc.forms.templates.update.mutationOptions({
			onError: (error) => {
				toast.error(`Failed to update form template: ${error.message}`);
			},
		}),
	);

	const addSolicitationFormMutation = useMutation(
		trpc.forms.solicitationForms.add.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.forms.solicitationForms.list.queryKey({ solicitationId: id }),
				});
			},
			onError: (error) => {
				toast.error(`Failed to add form: ${error.message}`);
			},
		}),
	);

	const updateSolicitationFormMutation = useMutation(
		trpc.forms.solicitationForms.update.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.forms.solicitationForms.list.queryKey({ solicitationId: id }),
				});
			},
			onError: (error) => {
				toast.error(`Failed to update form: ${error.message}`);
			},
		}),
	);

	const removeSolicitationFormMutation = useMutation(
		trpc.forms.solicitationForms.remove.mutationOptions({
			onSuccess: (data) => {
				toast.success(data.archived ? "Form archived" : "Form removed");
				queryClient.invalidateQueries({
					queryKey: trpc.forms.solicitationForms.list.queryKey({ solicitationId: id, includeArchived: true }),
				});
			},
			onError: (error) => {
				toast.error(`Failed to remove form: ${error.message}`);
			},
		}),
	);

	// Required documents state
	interface RequiredDocument {
		id: string;
		name: string;
		description: string;
		required: boolean;
	}
	// Solicitation-specific required documents (in addition to org-level)
	const [requiredDocuments, setRequiredDocuments] = useState<RequiredDocument[]>([]);
	const [newDocName, setNewDocName] = useState("");
	const [newDocDescription, setNewDocDescription] = useState("");
	const [newDocRequired, setNewDocRequired] = useState(true);

	// Compliance checks state
	interface ComplianceCheck {
		id: string;
		name: string;
		description: string;
		required: boolean;
		inherited: boolean;
		sourceOrgName?: string;
	}
	// Solicitation-specific compliance checks (in addition to org-level)
	const [solicitationComplianceChecks, setSolicitationComplianceChecks] = useState<ComplianceCheck[]>([]);
	const [newComplianceName, setNewComplianceName] = useState("");
	const [newComplianceDescription, setNewComplianceDescription] = useState("");
	const [newComplianceRequired, setNewComplianceRequired] = useState(true);

	// Evaluation criteria state
	interface EvaluationCriterion {
		id: string;
		title: string;
		description: string;
		maxPoints: number;
		sortOrder: number;
	}
	// Evaluation criteria - start empty, populated from org or custom
	const [criteria, setCriteria] = useState<EvaluationCriterion[]>([]);
	// Scoring weights for criteria (maps criterion ID to weight percentage)
	const [criteriaWeights, setCriteriaWeights] = useState<Record<string, number>>({});
	const [editingCriterion, setEditingCriterion] = useState<EvaluationCriterion | null>(null);
	const [criterionTitle, setCriterionTitle] = useState("");
	const [criterionDescription, setCriterionDescription] = useState("");
	const [criterionPoints, setCriterionPoints] = useState(10);

	// Note: Weights are initialized in the useEffect at lines 465-504 when
	// criteria are loaded from org or solicitation data. Additional weight
	// management for adding/removing criteria is handled by handleSaveCriterion
	// and handleDeleteCriterion functions.

	// Early returns - after all hooks
	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	if (!solicitation) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Solicitation not found</h2>
					<Button asChild className="mt-4">
						<Link to="/solicitations">Back to Solicitations</Link>
					</Button>
				</div>
			</DashboardLayout>
		);
	}

	const handleAddContractPeriod = () => {
		const nextOptionYear =
			contractPeriods.filter((p) => p.name.includes("Option Year")).length + 1;
		setContractPeriods([
			...contractPeriods,
			{
				id: crypto.randomUUID(),
				name: `Option Year ${nextOptionYear}`,
				startDate: "",
				endDate: "",
			},
		]);
	};

	const handleRemoveContractPeriod = (id: string) => {
		setContractPeriods(contractPeriods.filter((p) => p.id !== id));
	};

	const handleContractPeriodChange = (
		id: string,
		field: keyof ContractPeriod,
		value: string,
	) => {
		setContractPeriods(
			contractPeriods.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
		);
	};

	// Transform solicitation forms data
	const forms: SolicitationForm[] = (solicitationFormsData ?? []).map((sf) => ({
		id: sf.id,
		name: sf.title,
		description: sf.description ?? "",
		required: sf.is_required,
		formTemplateId: sf.form_template_id,
		pdfPath: null, // Legacy - will be null for new forms
		pdfStoragePath: null, // Will be fetched when editing
	}));

	// Handle viewing a form PDF in new tab
	const handleViewFormPdf = async (formTemplateId: string) => {
		try {
			const result = await trpcClient.pdfTemplates.getPdfUrl.query({
				templateId: formTemplateId,
			});
			if (result?.url) {
				window.open(result.url, "_blank");
			} else {
				toast.error("No PDF available for this form");
			}
		} catch (error) {
			console.error("Error viewing form:", error);
			toast.error("Failed to view form PDF");
		}
	};

	// Handle downloading a form PDF
	const handleDownloadFormPdf = async (formTemplateId: string, formName: string) => {
		try {
			const result = await trpcClient.pdfTemplates.getPdfUrl.query({
				templateId: formTemplateId,
			});
			if (result?.url) {
				// Fetch and download the file
				const response = await fetch(result.url);
				const blob = await response.blob();
				const blobUrl = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = blobUrl;
				link.download = `${formName}.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(blobUrl);
			} else {
				toast.error("No PDF available for this form");
			}
		} catch (error) {
			console.error("Error downloading form:", error);
			toast.error("Failed to download form PDF");
		}
	};

	// Handle opening form editor for editing an existing form
	const handleEditForm = async (form: SolicitationForm) => {
		setNewFormName(form.name);
		setNewFormDescription(form.description);
		setNewFormRequired(form.required);

		try {
			// Fetch the form template to get the pdf_storage_path
			const template = await trpcClient.forms.templates.get.query({
				id: form.formTemplateId,
			});

			// Update editingForm with the pdf_storage_path from the template
			setEditingForm({
				...form,
				pdfStoragePath: template.pdf_storage_path ?? null,
			});

			setFormEditorOpen(true);
		} catch (error) {
			console.error("Error loading form:", error);
			toast.error("Failed to load form for editing");
		}
	};

	// Handle adding a new form
	const handleAddNewForm = async (formData: {
		name: string;
		description: string;
		pdfStoragePath: string | null;
		formTemplateId?: string;
		widgets?: Array<{
			field_name: string;
			field_label: string;
			field_type: string;
			page_number: number;
			x: number;
			y: number;
			width: number;
			height: number;
			is_required: boolean;
			placeholder: string | null;
			description: string | null;
			options: string[] | null;
			font_size: number;
			tab_order: number;
		}>;
	}) => {
		if (!formData.pdfStoragePath || !user?.organizationId) return;

		try {
			// Use description from PdfFormEditor or the inline form
			const description = formData.description || newFormDescription;

			// 1. Create form template with Nutrient document ID
			const template = await createTemplateMutation.mutateAsync({
				title: newFormName || formData.name,
				description: description || undefined,
				pdfStoragePath: formData.pdfStoragePath,
				fieldConfig: [],
			});

			// 2. Save widgets to the template if any were defined
			if (formData.widgets && formData.widgets.length > 0) {
				await saveWidgetsMutation.mutateAsync({
					templateId: template.id,
					widgets: formData.widgets,
				});
			}

			// 3. Link to solicitation
			await addSolicitationFormMutation.mutateAsync({
				solicitationId: id,
				formTemplateId: template.id,
				title: newFormName || formData.name,
				description: description || undefined,
				isRequired: newFormRequired,
				sortOrder: forms.length,
			});

			toast.success("Form added");
		} catch (error) {
			console.error("Error adding form:", error);
			toast.error(error instanceof Error ? error.message : "Failed to add form");
		}
	};

	// Handle updating an existing form
	const handleUpdateExistingForm = async (formData: {
		name: string;
		description: string;
		pdfStoragePath: string | null;
		formTemplateId?: string;
	}) => {
		if (!editingForm || !user?.organizationId) return;

		try {
			// If there's a new/changed Nutrient document ID, update the template
			if (formData.pdfStoragePath) {
				// Update template with new Nutrient document ID
				await updateTemplateMutation.mutateAsync({
					id: editingForm.formTemplateId,
					title: newFormName || formData.name,
					pdfStoragePath: formData.pdfStoragePath,
				});
			} else {
				// Just update the title
				await updateTemplateMutation.mutateAsync({
					id: editingForm.formTemplateId,
					title: newFormName || formData.name,
				});
			}

			// Use description from PdfFormEditor or the inline form
			const description = formData.description || newFormDescription;

			// Update the solicitation form if needed
			const updates: { id: string; title?: string; description?: string; isRequired?: boolean } = {
				id: editingForm.id,
			};
			if ((newFormName || formData.name) !== editingForm.name) {
				updates.title = newFormName || formData.name;
			}
			if (description !== (editingForm.description ?? "")) {
				updates.description = description;
			}
			if (newFormRequired !== editingForm.required) {
				updates.isRequired = newFormRequired;
			}

			if (Object.keys(updates).length > 1) {
				await updateSolicitationFormMutation.mutateAsync(updates);
			}

			toast.success("Form updated");
			queryClient.invalidateQueries({
				queryKey: trpc.forms.solicitationForms.list.queryKey({ solicitationId: id }),
			});
		} catch (error) {
			console.error("Error updating form:", error);
			toast.error(error instanceof Error ? error.message : "Failed to update form");
		}
	};

	const handleDeleteForm = (formId: string) => {
		removeSolicitationFormMutation.mutate({ id: formId });
	};

	const handleToggleRequired = (formId: string) => {
		const form = forms.find((f) => f.id === formId);
		if (form) {
			updateSolicitationFormMutation.mutate({
				id: formId,
				isRequired: !form.required,
			});
		}
	};

	// Map inherited compliance checks from org query data
	const inheritedComplianceChecks: ComplianceCheck[] = (inheritedComplianceChecksData ?? []).map((c) => ({
		id: c.id,
		name: c.title,
		description: c.description ?? "",
		required: c.is_required,
		inherited: true,
		sourceOrgName: c.sourceOrganizationName,
	}));

	const handleAddComplianceCheck = () => {
		if (!newComplianceName.trim()) {
			toast.error("Please enter a compliance check name");
			return;
		}
		setSolicitationComplianceChecks([
			...solicitationComplianceChecks,
			{
				id: crypto.randomUUID(),
				name: newComplianceName,
				description: newComplianceDescription,
				required: newComplianceRequired,
				inherited: false,
			},
		]);
		setNewComplianceName("");
		setNewComplianceDescription("");
		setNewComplianceRequired(true);
		toast.success("Compliance check added");
	};

	const handleAddRequiredDocument = () => {
		if (!newDocName.trim()) {
			toast.error("Please enter a document name");
			return;
		}
		setRequiredDocuments([
			...requiredDocuments,
			{
				id: crypto.randomUUID(),
				name: newDocName,
				description: newDocDescription,
				required: newDocRequired,
			},
		]);
		setNewDocName("");
		setNewDocDescription("");
		setNewDocRequired(true);
		toast.success("Required document added");
	};

	const totalPoints = criteria.reduce((sum, c) => sum + c.maxPoints, 0);
	const totalWeight = criteria.reduce((sum, c) => sum + (criteriaWeights[c.id] ?? 0), 0);

	// Weights are invalid if there are custom criteria and they don't sum to 100
	const hasCustomCriteria = criteria.length > 0 && (!hasOrgCriteria || !useOrgCriteria);
	const weightsInvalid = hasCustomCriteria && totalWeight !== 100;

	const handleAddCriterion = () => {
		if (!criterionTitle.trim()) {
			toast.error("Please enter a criterion title");
			return;
		}
		const newCriterion = {
			id: crypto.randomUUID(),
			title: criterionTitle,
			description: criterionDescription,
			maxPoints: criterionPoints,
			sortOrder: criteria.length + 1,
		};
		const updatedCriteria = [...criteria, newCriterion];
		setCriteria(updatedCriteria);

		// Auto-rebalance weights: distribute equally among all criteria
		const numCriteria = updatedCriteria.length;
		const equalWeight = Math.floor(100 / numCriteria);
		const remainder = 100 - equalWeight * numCriteria;

		const newWeights: Record<string, number> = {};
		updatedCriteria.forEach((c, index) => {
			// Give the remainder to the first criterion to ensure total is exactly 100
			newWeights[c.id] = equalWeight + (index === 0 ? remainder : 0);
		});
		setCriteriaWeights(newWeights);

		setCriterionTitle("");
		setCriterionDescription("");
		setCriterionPoints(10);
		toast.success("Criterion added");
	};

	const handleUpdateCriterion = () => {
		if (!editingCriterion || !criterionTitle.trim()) return;
		setCriteria(
			criteria.map((c) =>
				c.id === editingCriterion.id
					? {
							...c,
							title: criterionTitle,
							description: criterionDescription,
							maxPoints: criterionPoints,
						}
					: c,
			),
		);
		setEditingCriterion(null);
		setCriterionTitle("");
		setCriterionDescription("");
		setCriterionPoints(10);
		toast.success("Criterion updated");
	};

	const handleDeleteCriterion = (id: string) => {
		const updatedCriteria = criteria.filter((c) => c.id !== id);
		setCriteria(updatedCriteria);

		// Auto-rebalance weights after deletion
		if (updatedCriteria.length > 0) {
			const numCriteria = updatedCriteria.length;
			const equalWeight = Math.floor(100 / numCriteria);
			const remainder = 100 - equalWeight * numCriteria;

			const newWeights: Record<string, number> = {};
			updatedCriteria.forEach((c, index) => {
				newWeights[c.id] = equalWeight + (index === 0 ? remainder : 0);
			});
			setCriteriaWeights(newWeights);
		} else {
			setCriteriaWeights({});
		}

		toast.success("Criterion removed");
	};

	const handleEditCriterion = (criterion: EvaluationCriterion) => {
		setEditingCriterion(criterion);
		setCriterionTitle(criterion.title);
		setCriterionDescription(criterion.description);
		setCriterionPoints(criterion.maxPoints);
	};

	const handleWeightChange = (id: string, value: number) => {
		// Simply update the weight for this criterion without affecting others
		setCriteriaWeights(prev => ({ ...prev, [id]: value }));
	};

	const handleSave = async () => {
		if (!formData.title.trim()) {
			toast.error("Please enter a solicitation title");
			setActiveTab("details");
			return;
		}

		setIsSaving(true);

		try {
			await updateSolicitationMutation.mutateAsync({
				id,
				title: formData.title,
				description: formData.description || null,
				type: formData.type ? (formData.type as "RFP" | "RFQ" | "RFI" | "IFB" | "ITB") : undefined,
				publishDate: localToUtcIso(formData.publishDate, formData.publishTime) ?? null,
				dueDate: localToUtcIso(formData.dueDate, formData.dueTime) ?? null,
				qaDeadline: localToUtcIso(formData.qaDeadlineDate, formData.qaDeadlineTime) ?? null,
				memberOrgId: formData.memberOrgId || null,
				pointOfContactId: formData.pointOfContactId || null,
			});

			// Save smart codes (filter out empty ones)
			const validSmartCodes = smartCodes.filter((sc) => sc.trim());
			await updateSmartCodesMutation.mutateAsync({
				solicitationId: id,
				values: validSmartCodes,
			});

			// Save milestones - delete removed, update existing, create new
			// First delete any milestones that were removed
			const existingMilestoneIds = new Set((existingMilestones ?? []).map((m) => m.id));
			const currentMilestoneIds = new Set(milestones.filter((m) => m.id).map((m) => m.id));
			for (const existingId of existingMilestoneIds) {
				if (!currentMilestoneIds.has(existingId)) {
					await deleteMilestoneMutation.mutateAsync({ id: existingId });
				}
			}
			// Update existing milestones and create new ones
			for (const milestone of milestones) {
				if (milestone.title.trim()) {
					const dateTimeUtc = localToUtcIso(milestone.date, milestone.time);
					if (milestone.id) {
						// Update existing milestone
						await updateMilestoneMutation.mutateAsync({
							id: milestone.id,
							title: milestone.title,
							date: dateTimeUtc || undefined,
							isMeeting: milestone.isMeeting,
							meetingLink: milestone.meetingLink || null,
							meetingLocation: milestone.meetingAddress || null,
							isMandatory: milestone.isRequired,
						});
					} else if (dateTimeUtc) {
						// Create new milestone
						await createMilestoneMutation.mutateAsync({
							solicitationId: id,
							title: milestone.title,
							date: dateTimeUtc,
							isMeeting: milestone.isMeeting,
							meetingLink: milestone.meetingLink || null,
							meetingLocation: milestone.meetingAddress || null,
							isMandatory: milestone.isRequired,
						});
					}
				}
			}

			// Save solicitation-specific compliance checks
			const validChecks = solicitationComplianceChecks
				.filter((c) => c.name.trim())
				.map((c, index) => ({
					id: c.id.startsWith("temp-") ? undefined : c.id,
					title: c.name,
					description: c.description || null,
					isRequired: c.required,
					sortOrder: index,
				}));
			await updateComplianceChecksMutation.mutateAsync({
				solicitationId: id,
				checks: validChecks,
			});

			// Save solicitation-specific required documents
			const existingDocIds = new Set((existingRequiredDocs ?? []).map((d) => d.id));
			const currentDocIds = new Set(requiredDocuments.map((d) => d.id));

			// Delete removed documents
			for (const existingId of existingDocIds) {
				if (!currentDocIds.has(existingId)) {
					await deleteRequiredDocMutation.mutateAsync({ id: existingId });
				}
			}

			// Create new documents (ones that don't exist in DB yet)
			for (const doc of requiredDocuments) {
				if (!existingDocIds.has(doc.id) && doc.name.trim()) {
					await createRequiredDocMutation.mutateAsync({
						solicitationId: id,
						title: doc.name,
						description: doc.description || null,
						isRequired: doc.required,
						sortOrder: requiredDocuments.indexOf(doc),
					});
				}
			}

			// Save solicitation evaluation criteria
			// Always save criteria (even when using org criteria) so they persist when unchecked
			const criteriaToSave = criteria.map((c, index) => ({
				id: c.id,
				title: c.title,
				description: c.description || null,
				maxPoints: c.maxPoints,
				weight: criteriaWeights[c.id] || 0,
				sortOrder: index,
			}));
			const useOrgCriteriaValue = hasOrgCriteria ? useOrgCriteria : false;
			console.log("[DEBUG] Saving eval criteria - useOrgCriteria:", useOrgCriteriaValue, "hasOrgCriteria:", hasOrgCriteria, "useOrgCriteria state:", useOrgCriteria);
			await saveEvalCriteriaMutation.mutateAsync({
				solicitationId: id,
				useOrgCriteria: useOrgCriteriaValue,
				criteria: criteriaToSave,
			});
			// Invalidate the query cache to ensure fresh data on reload
			queryClient.invalidateQueries({
				queryKey: trpc.solicitations.listEvalCriteria.queryKey({ solicitationId: id }),
			});

			// Save contract periods
			const periodsToSave = contractPeriods
				.filter((p) => p.name.trim())
				.map((p, index) => ({
					id: p.id,
					name: p.name,
					startDate: p.startDate || null,
					endDate: p.endDate || null,
					sortOrder: index,
				}));
			await saveContractPeriodsMutation.mutateAsync({
				solicitationId: id,
				periods: periodsToSave,
			});

			toast.success("Changes saved successfully!");
		} catch (error) {
			console.error("Error saving solicitation:", error);
			toast.error(error instanceof Error ? error.message : "Failed to save changes");
		} finally {
			setIsSaving(false);
		}
	};

	const handlePublish = async () => {
		setIsPublishing(true);
		try {
			// First save the current changes
			await handleSave();

			// If already published, just save - don't call publish again
			if (isPublished) {
				toast.success("Solicitation updated successfully!");
				navigate({ to: "/solicitations/$id", params: { id } });
				return;
			}

			// For draft/scheduled, publish the solicitation (sets publish_date to now)
			await publishMutation.mutateAsync({ id });

			toast.success("Solicitation published successfully!");
			navigate({ to: "/solicitations/$id", params: { id } });
		} catch (error) {
			console.error("Error publishing solicitation:", error);
			toast.error(error instanceof Error ? error.message : "Failed to publish solicitation");
		} finally {
			setIsPublishing(false);
		}
	};

	const handleSchedule = async () => {
		// Validate that publish date is set and in the future
		const publishDateIso = localToUtcIso(formData.publishDate, formData.publishTime);
		if (!publishDateIso) {
			toast.error("Please set a publish date before scheduling");
			return;
		}
		if (new Date(publishDateIso) <= new Date()) {
			toast.error("Publish date must be in the future to schedule");
			return;
		}

		setIsPublishing(true);
		try {
			// First save the current changes
			await handleSave();

			// Then publish with the future date (creates "scheduled" status)
			await publishMutation.mutateAsync({ id, publishDate: publishDateIso });

			toast.success("Solicitation scheduled successfully!");
			navigate({ to: "/solicitations/$id", params: { id } });
		} catch (error) {
			console.error("Error scheduling solicitation:", error);
			toast.error(error instanceof Error ? error.message : "Failed to schedule solicitation");
		} finally {
			setIsPublishing(false);
		}
	};

	if (!solicitation) {
		return (
			<DashboardLayout>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Solicitation not found</h2>
					<Button asChild className="mt-4">
						<Link to="/solicitations">Back to Solicitations</Link>
					</Button>
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<Button variant="ghost" size="icon" asChild>
							<Link to="/solicitations/$id" params={{ id }}>
								<ArrowLeft className="h-4 w-4" />
							</Link>
						</Button>
						<div>
							<h1 className="text-3xl font-bold tracking-tight">
								Edit Solicitation
							</h1>
							<p className="text-muted-foreground">{solicitation.title}</p>
						</div>
					</div>
					<div className="flex gap-2">
						{!isPublished && (
							<Button variant="outline" onClick={handleSave} disabled={isSaving || isPublishing || weightsInvalid}>
								{isSaving ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<Save className="mr-2 h-4 w-4" />
								)}
								{isSaving ? "Saving..." : "Save Draft"}
							</Button>
						)}
						{(() => {
							const publishDateIso = localToUtcIso(formData.publishDate, formData.publishTime);
							const isScheduled = !isPublished && publishDateIso && new Date(publishDateIso) > new Date();

							// For published solicitations, just show "Update"
							if (isPublished) {
								return (
									<Button
										onClick={handlePublish}
										disabled={isSaving || isPublishing || weightsInvalid}
									>
										{isPublishing ? (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										) : (
											<Save className="mr-2 h-4 w-4" />
										)}
										{isPublishing ? "Updating..." : "Update"}
									</Button>
								);
							}

							return (
								<Button
									onClick={isScheduled ? handleSchedule : handlePublish}
									disabled={isSaving || isPublishing || weightsInvalid}
								>
									{isPublishing ? (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									) : isScheduled ? (
										<Clock className="mr-2 h-4 w-4" />
									) : (
										<Send className="mr-2 h-4 w-4" />
									)}
									{isPublishing ? (isScheduled ? "Scheduling..." : "Publishing...") : (isScheduled ? "Schedule" : "Publish")}
								</Button>
							);
						})()}
					</div>
				</div>

				{/* Form Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList>
						<TabsTrigger value="details">Basic Details</TabsTrigger>
						<TabsTrigger value="dates">Dates & Milestones</TabsTrigger>
						<TabsTrigger value="attachments">Attachments</TabsTrigger>
						<TabsTrigger value="requirements">Requirements</TabsTrigger>
						<TabsTrigger value="evaluation">Evaluation</TabsTrigger>
					</TabsList>

					<TabsContent value="details" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Solicitation Information</CardTitle>
								<CardDescription>
									Update basic information about the solicitation
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2 md:col-span-2">
										<Label htmlFor="title">Title *</Label>
										<Input
											id="title"
											value={formData.title}
											onChange={(e) =>
												setFormData({ ...formData, title: e.target.value })
											}
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="type">Solicitation Type *</Label>
										<Select
											value={formData.type}
											onValueChange={(value) =>
												setFormData({ ...formData, type: value })
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="RFP">
													Request for Proposal (RFP)
												</SelectItem>
												<SelectItem value="RFQ">
													Request for Quote (RFQ)
												</SelectItem>
												<SelectItem value="RFI">
													Request for Information (RFI)
												</SelectItem>
												<SelectItem value="IFB">
													Invitation for Bid (IFB)
												</SelectItem>
												<SelectItem value="ITB">
													Invitation to Bid (ITB)
												</SelectItem>
											</SelectContent>
										</Select>
									</div>

									<div className="space-y-2 md:col-span-2">
										<Label htmlFor="description">Description *</Label>
										<Textarea
											id="description"
											rows={5}
											value={formData.description}
											onChange={(e) =>
												setFormData({
													...formData,
													description: e.target.value,
												})
											}
										/>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Organization & Contact</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="memberOrg">Department / Member Org</Label>
										<Select
											value={formData.memberOrgId}
											onValueChange={(value) =>
												setFormData({ ...formData, memberOrgId: value, pointOfContactId: "" })
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select department (optional)" />
											</SelectTrigger>
											<SelectContent>
												{(accessibleOrgs ?? []).map((org) => (
													<SelectItem key={org.id} value={org.id}>
														{org.name}
														{org.type !== "buyer" && (
															<span className="text-muted-foreground ml-1">
																({org.type})
															</span>
														)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											Optionally assign this solicitation to a specific department
										</p>
									</div>

									<div className="space-y-2">
										<Label htmlFor="pointOfContact">Point of Contact</Label>
										<Select
											value={formData.pointOfContactId}
											onValueChange={(value) =>
												setFormData({ ...formData, pointOfContactId: value })
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select point of contact" />
											</SelectTrigger>
											<SelectContent>
												{(orgMembers ?? []).map((member) => (
													<SelectItem key={member.user_id} value={member.user_id}>
														{member.profiles?.first_name} {member.profiles?.last_name}
														{member.profiles?.email && (
															<span className="text-muted-foreground ml-1">
																({member.profiles.email})
															</span>
														)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											The person vendors can contact with questions
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Smart Codes</CardTitle>
								<CardDescription>
									Categorization codes for this solicitation
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-3">
									{smartCodes.map((smartCode, index) => (
										<div key={index} className="flex items-center gap-2">
											<Input
												placeholder="e.g., 541512 - Computer Systems Design Services"
												value={smartCode}
												onChange={(e) =>
													handleSmartCodeChange(index, e.target.value)
												}
												className="flex-1"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() => handleRemoveSmartCode(index)}
											>
												<X className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleAddSmartCode}
								>
									<Plus className="mr-2 h-4 w-4" />
									Add Smart Code
								</Button>
								<p className="text-sm text-muted-foreground">
									Enter classification codes for this solicitation.
								</p>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="dates" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Key Dates</CardTitle>
								<CardDescription>
									All times shown in your local timezone ({timezoneAbbr})
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="publishDate">Publish Date</Label>
										<Input
											id="publishDate"
											type="date"
											value={formData.publishDate}
											onChange={(e) =>
												setFormData({
													...formData,
													publishDate: e.target.value,
												})
											}
										/>
										<Label htmlFor="publishTime">Publish Time</Label>
										<Input
											id="publishTime"
											type="time"
											value={formData.publishTime}
											onChange={(e) =>
												setFormData({
													...formData,
													publishTime: e.target.value,
												})
											}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="dueDate">Due Date *</Label>
										<Input
											id="dueDate"
											type="date"
											value={formData.dueDate}
											onChange={(e) =>
												setFormData({ ...formData, dueDate: e.target.value })
											}
										/>
										<Label htmlFor="dueTime">Due Time *</Label>
										<Input
											id="dueTime"
											type="time"
											value={formData.dueTime}
											onChange={(e) =>
												setFormData({ ...formData, dueTime: e.target.value })
											}
										/>
									</div>
								</div>
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="qaDeadlineDate">Q&A Deadline (Optional)</Label>
										<Input
											id="qaDeadlineDate"
											type="date"
											value={formData.qaDeadlineDate}
											onChange={(e) =>
												setFormData({
													...formData,
													qaDeadlineDate: e.target.value,
												})
											}
										/>
										<Label htmlFor="qaDeadlineTime">Q&A Deadline Time</Label>
										<Input
											id="qaDeadlineTime"
											type="time"
											value={formData.qaDeadlineTime}
											onChange={(e) =>
												setFormData({
													...formData,
													qaDeadlineTime: e.target.value,
												})
											}
										/>
										<p className="text-xs text-muted-foreground">
											Vendors can only submit questions before this deadline
										</p>
									</div>
									<div />
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Contract Periods</CardTitle>
									<CardDescription>
										Define performance periods for this contract (optional)
									</CardDescription>
								</div>
								<Button variant="outline" onClick={handleAddContractPeriod}>
									<Plus className="mr-2 h-4 w-4" />
									Add Period
								</Button>
							</CardHeader>
							<CardContent className="space-y-4">
								{contractPeriods.length === 0 ? (
									<p className="text-sm text-muted-foreground text-center py-4">
										No contract periods defined. Click "Add Period" to add one.
									</p>
								) : contractPeriods.map((period, index) => (
									<div
										key={period.id}
										className="flex items-start gap-4 p-4 border rounded-lg"
									>
										<div className="flex-1 space-y-4">
											<div className="grid gap-4 md:grid-cols-3">
												<div className="space-y-2">
													<Label>Period Name</Label>
													<Input
														placeholder="e.g., Base Contract"
														value={period.name}
														onChange={(e) =>
															handleContractPeriodChange(
																period.id,
																"name",
																e.target.value,
															)
														}
													/>
												</div>
												<div className="space-y-2">
													<Label>Start Date</Label>
													<Input
														type="date"
														value={period.startDate}
														onChange={(e) =>
															handleContractPeriodChange(
																period.id,
																"startDate",
																e.target.value,
															)
														}
													/>
												</div>
												<div className="space-y-2">
													<Label>End Date</Label>
													<Input
														type="date"
														value={period.endDate}
														onChange={(e) =>
															handleContractPeriodChange(
																period.id,
																"endDate",
																e.target.value,
															)
														}
													/>
												</div>
											</div>
										</div>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => handleRemoveContractPeriod(period.id)}
										>
											<X className="h-4 w-4" />
										</Button>
									</div>
								))}
							</CardContent>
						</Card>

						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Custom Milestones</CardTitle>
									<CardDescription>
										Add additional milestones like Q&A deadlines or pre-bid
										conferences. Times shown in {timezoneAbbr}.
									</CardDescription>
								</div>
								<Button variant="outline" onClick={addMilestone}>
									<Plus className="mr-2 h-4 w-4" />
									Add Milestone
								</Button>
							</CardHeader>
							<CardContent className="space-y-4">
								{milestones.length === 0 ? (
									<p className="text-sm text-muted-foreground text-center py-4">
										No custom milestones added yet. Click "Add Milestone" to create one.
									</p>
								) : (
									milestones.map((milestone, index) => (
										<div
											key={index}
											className="flex items-start gap-4 p-4 border rounded-lg"
										>
											<div className="flex-1 space-y-4">
												<div className="grid gap-4 md:grid-cols-3">
													<div className="space-y-2">
														<Label>Title</Label>
														<Input
															placeholder="Milestone title"
															value={milestone.title}
															onChange={(e) => {
																const updated = [...milestones];
																updated[index].title = e.target.value;
																setMilestones(updated);
															}}
														/>
													</div>
													<div className="space-y-2">
														<Label>Date</Label>
														<Input
															type="date"
															value={milestone.date}
															onChange={(e) => {
																const updated = [...milestones];
																updated[index].date = e.target.value;
																setMilestones(updated);
															}}
														/>
														<Label>Time</Label>
														<Input
															type="time"
															value={milestone.time}
															onChange={(e) => {
																const updated = [...milestones];
																updated[index].time = e.target.value;
																setMilestones(updated);
															}}
														/>
													</div>
													<div className="space-y-2">
														<Label>Type</Label>
														<div className="flex items-center gap-2 pt-2">
															<Checkbox
																checked={milestone.isMeeting}
																onCheckedChange={(checked) => {
																	const updated = [...milestones];
																	updated[index].isMeeting = checked as boolean;
																	setMilestones(updated);
																}}
															/>
															<span className="text-sm">This is a meeting</span>
														</div>
													</div>
												</div>
												{milestone.isMeeting && (
													<div className="grid gap-4 md:grid-cols-3 pl-4 border-l-2 border-muted">
														<div className="space-y-2">
															<div className="flex items-center gap-2">
																<Checkbox
																	checked={milestone.isRequired}
																	onCheckedChange={(checked) => {
																		const updated = [...milestones];
																		updated[index].isRequired = checked as boolean;
																		setMilestones(updated);
																	}}
																/>
																<Label className="font-normal">
																	Attendance Required
																</Label>
															</div>
														</div>
														<div className="space-y-2">
															<Label>Meeting Address</Label>
															<Input
																placeholder="123 Main St, City, State"
																value={milestone.meetingAddress}
																onChange={(e) => {
																	const updated = [...milestones];
																	updated[index].meetingAddress = e.target.value;
																	setMilestones(updated);
																}}
															/>
														</div>
														<div className="space-y-2">
															<Label>Meeting Link</Label>
															<Input
																placeholder="https://zoom.us/..."
																value={milestone.meetingLink}
																onChange={(e) => {
																	const updated = [...milestones];
																	updated[index].meetingLink = e.target.value;
																	setMilestones(updated);
																}}
															/>
														</div>
													</div>
												)}
											</div>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => removeMilestone(index)}
											>
												<X className="h-4 w-4" />
											</Button>
										</div>
									))
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="attachments" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Solicitation Attachments</CardTitle>
								<CardDescription>
									Reference documents for vendors (
									{attachments?.length ?? 0} files)
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Hidden file input */}
								<input
									ref={fileInputRef}
									type="file"
									multiple
									accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
									className="hidden"
									onChange={handleFileSelect}
								/>

								{/* Upload zone */}
								<div
									className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors"
									onClick={() => fileInputRef.current?.click()}
								>
									{isUploadingAttachment ? (
										<>
											<Loader2 className="h-10 w-10 mx-auto text-muted-foreground mb-4 animate-spin" />
											<p className="font-medium">Uploading...</p>
										</>
									) : (
										<>
											<Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
											<p className="font-medium">
												Click to upload or drag and drop
											</p>
											<p className="text-sm text-muted-foreground">
												PDF, DOC, DOCX, XLS, XLSX up to 500MB
											</p>
										</>
									)}
								</div>

								{/* Attachments list */}
								{attachmentsLoading ? (
									<div className="text-center py-8">
										<Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
									</div>
								) : attachments && attachments.length > 0 ? (
									<div className="space-y-3">
										<Label>Uploaded Files ({attachments.length})</Label>
										{attachments.map((attachment) => (
											<div
												key={attachment.id}
												className="flex items-center justify-between p-4 border rounded-lg"
											>
												<div className="flex items-center gap-3">
													<FileText className="h-5 w-5 text-muted-foreground" />
													<div>
														<p className="font-medium">{attachment.title}</p>
														<p className="text-sm text-muted-foreground">
															{attachment.file_size
																? `${(attachment.file_size / 1024 / 1024).toFixed(2)} MB`
																: "Unknown size"}
															{attachment.mime_type && ` • ${attachment.mime_type.split("/")[1]?.toUpperCase() || attachment.mime_type}`}
														</p>
													</div>
												</div>
												<div className="flex items-center gap-1">
													{isPdfFile(attachment.file_path) && (
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => handleViewAttachment(attachment.file_path)}
																>
																	<Eye className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>View PDF</TooltipContent>
														</Tooltip>
													)}
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="icon"
																onClick={() =>
																	handleDownloadAttachment(
																		attachment.file_path,
																		attachment.title,
																	)
																}
															>
																<Download className="h-4 w-4" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>Download: {attachment.title}</TooltipContent>
													</Tooltip>
													<Button
														variant="ghost"
														size="icon"
														onClick={() => handleDeleteAttachment(attachment.id)}
														disabled={deleteAttachmentMutation.isPending}
														title={isPublished ? "Archive attachment" : "Delete attachment"}
													>
														{deleteAttachmentMutation.isPending ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : isPublished ? (
															<Archive className="h-4 w-4" />
														) : (
															<Trash2 className="h-4 w-4" />
														)}
													</Button>
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										<FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No attachments uploaded yet.</p>
									</div>
								)}

								{/* Show Archived attachments */}
								{archivedAttachments.length > 0 && (
									<Collapsible className="mt-4">
										<CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
											<ChevronDown className="h-4 w-4" />
											Show Archived ({archivedAttachments.length})
										</CollapsibleTrigger>
										<CollapsibleContent className="mt-2 space-y-2 pl-6 border-l-2 border-muted">
											{archivedAttachments.map((attachment) => (
												<div
													key={attachment.id}
													className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 opacity-60"
												>
													<div className="flex items-center gap-3">
														<FileText className="h-5 w-5 text-muted-foreground" />
														<div>
															<p className="font-medium line-through">{attachment.title}</p>
															<p className="text-sm text-muted-foreground">
																Archived
																{attachment.file_size
																	? ` • ${(attachment.file_size / 1024 / 1024).toFixed(2)} MB`
																	: ""}
															</p>
														</div>
													</div>
													<div className="flex items-center gap-1">
														{isPdfFile(attachment.file_path) && (
															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		variant="ghost"
																		size="icon"
																		onClick={() => handleViewAttachment(attachment.file_path)}
																	>
																		<Eye className="h-4 w-4" />
																	</Button>
																</TooltipTrigger>
																<TooltipContent>View PDF</TooltipContent>
															</Tooltip>
														)}
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() =>
																		handleDownloadAttachment(
																			attachment.file_path,
																			attachment.title,
																		)
																	}
																>
																	<Download className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>Download: {attachment.title}</TooltipContent>
														</Tooltip>
													</div>
												</div>
											))}
										</CollapsibleContent>
									</Collapsible>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="requirements" className="space-y-6">
						{/* Vendor Forms */}
						<Card>
							<CardHeader>
								<CardTitle>Vendor Forms</CardTitle>
								<CardDescription>
									Fillable PDF forms that vendors must complete during bid
									submission
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Add new form section */}
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">Add New Form</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Form Title *</Label>
											<Input
												placeholder="e.g., Vendor Information Form"
												value={newFormName}
												onChange={(e) => setNewFormName(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label>Required</Label>
											<div className="flex items-center gap-2 pt-2">
												<Checkbox
													checked={newFormRequired}
													onCheckedChange={(checked) =>
														setNewFormRequired(checked as boolean)
													}
												/>
												<span className="text-sm">
													Vendors must complete this form
												</span>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<Label>Description</Label>
										<Textarea
											placeholder="Describe what this form is for..."
											value={newFormDescription}
											onChange={(e) => setNewFormDescription(e.target.value)}
											rows={2}
										/>
									</div>
									<Button
										onClick={() => {
											if (!newFormName.trim()) {
												toast.error("Please enter a form title");
												return;
											}
											setEditingForm(null);
											setFormEditorOpen(true);
										}}
									>
										<Plus className="mr-2 h-4 w-4" />
										Create Form PDF
									</Button>
								</div>

								{/* Inherited forms from organization */}
								{inheritedOrgForms && inheritedOrgForms.length > 0 && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<Building2 className="h-4 w-4 text-muted-foreground" />
											<Label className="text-muted-foreground">
												Inherited from Organization ({inheritedOrgForms.length})
											</Label>
										</div>
										{inheritedOrgForms.map((form) => (
											<div
												key={form.id}
												className="flex items-start justify-between p-4 border rounded-lg bg-muted/20"
											>
												<div className="flex items-start gap-3">
													<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2 flex-wrap">
															<span className="font-medium">{form.title}</span>
															<Badge
																variant={form.is_required ? "default" : "outline"}
															>
																{form.is_required ? "Required" : "Optional"}
															</Badge>
															{form.sourceOrganizationName && (
																<Badge variant="secondary">
																	From: {form.sourceOrganizationName}
																</Badge>
															)}
														</div>
														{form.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{form.description}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Existing forms list (solicitation-specific) */}
								{forms.length > 0 && (
									<div className="space-y-3 mt-6">
										<Label>
											{inheritedOrgForms && inheritedOrgForms.length > 0
												? `Solicitation-Specific Forms (${forms.length})`
												: `Created Forms (${forms.length})`}
										</Label>
										{forms.map((form) => (
											<div
												key={form.id}
												className="flex items-start justify-between p-4 border rounded-lg"
											>
												<div className="flex items-start gap-3">
													<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<span className="font-medium">{form.name}</span>
														{form.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{form.description}
															</p>
														)}
													</div>
												</div>
												<div className="flex items-center gap-3">
													{!isPublished && (
														<div className="flex items-center gap-2">
															<Checkbox
																id={`form-required-${form.id}`}
																checked={form.required}
																onCheckedChange={() => handleToggleRequired(form.id)}
																disabled={updateSolicitationFormMutation.isPending}
															/>
															<Label
																htmlFor={`form-required-${form.id}`}
																className="text-sm font-normal cursor-pointer"
															>
																Required
															</Label>
														</div>
													)}
													{isPublished && form.required && (
														<Badge variant="secondary" className="text-xs">Required</Badge>
													)}
													<div className="flex items-center gap-1">
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => handleViewFormPdf(form.formTemplateId)}
																>
																	<Eye className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>View PDF</TooltipContent>
														</Tooltip>
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	onClick={() => handleDownloadFormPdf(form.formTemplateId, form.name)}
																>
																	<Download className="h-4 w-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent>Download: {form.name}</TooltipContent>
														</Tooltip>
														{!isPublished && (
															<Button
																variant="ghost"
																size="icon"
																onClick={() => handleEditForm(form)}
																title="Edit form"
															>
																<Pencil className="h-4 w-4" />
															</Button>
														)}
														<Button
															variant="ghost"
															size="icon"
															disabled={removeSolicitationFormMutation.isPending}
															onClick={() => handleDeleteForm(form.id)}
															title={isPublished ? "Archive form" : "Remove form"}
														>
															{isPublished ? (
																<Archive className="h-4 w-4" />
															) : (
																<Trash2 className="h-4 w-4" />
															)}
														</Button>
													</div>
												</div>
											</div>
										))}
									</div>
								)}

								{isLoadingForms ? (
									<div className="text-center py-8">
										<Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
									</div>
								) : forms.length === 0 && (!inheritedOrgForms || inheritedOrgForms.length === 0) ? (
									<div className="text-center py-8 text-muted-foreground">
										<FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No forms added yet. Create your first form above.</p>
									</div>
								) : null}

								{/* Show Archived forms */}
								{archivedSolicitationFormsData.length > 0 && (
									<Collapsible className="mt-4">
										<CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
											<ChevronDown className="h-4 w-4" />
											Show Archived ({archivedSolicitationFormsData.length})
										</CollapsibleTrigger>
										<CollapsibleContent className="mt-2 space-y-2 pl-6 border-l-2 border-muted">
											{archivedSolicitationFormsData.map((form) => (
												<div
													key={form.id}
													className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 opacity-60"
												>
													<div className="flex items-center gap-3">
														<FileText className="h-5 w-5 text-muted-foreground" />
														<div>
															<p className="font-medium line-through">{form.title}</p>
															<p className="text-sm text-muted-foreground">
																Archived
																{form.is_required && " • Was required"}
															</p>
														</div>
													</div>
												</div>
											))}
										</CollapsibleContent>
									</Collapsible>
								)}
							</CardContent>
						</Card>

						{/* Required Documents */}
						<Card>
							<CardHeader>
								<CardTitle>Required Documents</CardTitle>
								<CardDescription>
									Documents vendors must upload with their submission (not
									fillable forms)
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Inherited documents from organization */}
								{inheritedRequiredDocs && inheritedRequiredDocs.length > 0 && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<Building2 className="h-4 w-4 text-muted-foreground" />
											<Label className="text-muted-foreground">
												Inherited from Organization ({inheritedRequiredDocs.length})
											</Label>
										</div>
										{inheritedRequiredDocs.map((doc) => (
											<div
												key={doc.id}
												className="flex items-start justify-between p-4 border rounded-lg bg-muted/20"
											>
												<div className="flex items-start gap-3">
													<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2 flex-wrap">
															<span className="font-medium">{doc.title}</span>
															<Badge
																variant={doc.is_required ? "default" : "outline"}
															>
																{doc.is_required ? "Required" : "Optional"}
															</Badge>
															{doc.sourceOrganizationName && (
																<Badge variant="secondary">
																	From: {doc.sourceOrganizationName}
																</Badge>
															)}
														</div>
														{doc.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{doc.description}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Add new required document */}
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">Add Document Requirement</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Document Name *</Label>
											<Input
												placeholder="e.g., Insurance Certificate"
												value={newDocName}
												onChange={(e) => setNewDocName(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label>Required</Label>
											<div className="flex items-center gap-2 pt-2">
												<Checkbox
													checked={newDocRequired}
													onCheckedChange={(checked) =>
														setNewDocRequired(checked as boolean)
													}
												/>
												<span className="text-sm">
													Vendors must upload this document
												</span>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<Label>Description</Label>
										<Textarea
											placeholder="Describe what this document should contain..."
											value={newDocDescription}
											onChange={(e) => setNewDocDescription(e.target.value)}
											rows={2}
										/>
									</div>
									<Button onClick={handleAddRequiredDocument}>
										<Plus className="mr-2 h-4 w-4" />
										Add Document Requirement
									</Button>
								</div>

								{/* Existing required documents list */}
								{requiredDocuments.length > 0 && (
									<div className="space-y-3 mt-6">
										<Label>
											Document Requirements ({requiredDocuments.length})
										</Label>
										{requiredDocuments.map((doc) => (
											<div
												key={doc.id}
												className="flex items-start justify-between p-4 border rounded-lg"
											>
												<div className="flex items-start gap-3">
													<Upload className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{doc.name}</span>
															<Badge
																variant={doc.required ? "default" : "outline"}
															>
																{doc.required ? "Required" : "Optional"}
															</Badge>
														</div>
														{doc.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{doc.description}
															</p>
														)}
													</div>
												</div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setRequiredDocuments(
															requiredDocuments.filter((d) => d.id !== doc.id),
														);
														toast.success(isPublished ? "Document requirement will be archived on save" : "Document requirement removed");
													}}
													title={isPublished ? "Archive requirement" : "Remove requirement"}
												>
													{isPublished ? (
														<Archive className="h-4 w-4" />
													) : (
														<Trash2 className="h-4 w-4" />
													)}
												</Button>
											</div>
										))}
									</div>
								)}

								{requiredDocuments.length === 0 && (
									<div className="text-center py-8 text-muted-foreground">
										<Upload className="h-12 w-12 mx-auto mb-3 opacity-50" />
										<p>No document requirements added yet.</p>
									</div>
								)}

								{/* Show Archived required documents */}
								{archivedRequiredDocs.length > 0 && (
									<Collapsible className="mt-4">
										<CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
											<ChevronDown className="h-4 w-4" />
											Show Archived ({archivedRequiredDocs.length})
										</CollapsibleTrigger>
										<CollapsibleContent className="mt-2 space-y-2 pl-6 border-l-2 border-muted">
											{archivedRequiredDocs.map((doc) => (
												<div
													key={doc.id}
													className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 opacity-60"
												>
													<div className="flex items-center gap-3">
														<Upload className="h-5 w-5 text-muted-foreground" />
														<div>
															<p className="font-medium line-through">{doc.title}</p>
															<p className="text-sm text-muted-foreground">
																Archived
																{doc.is_required && " • Was required"}
															</p>
														</div>
													</div>
												</div>
											))}
										</CollapsibleContent>
									</Collapsible>
								)}
							</CardContent>
						</Card>

						{/* Compliance Checks */}
						<Card>
							<CardHeader>
								<CardTitle>Compliance Checks</CardTitle>
								<CardDescription>
									Requirements vendors must meet (certifications, registrations,
									etc.)
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Inherited checks from organization */}
								{inheritedComplianceChecks.length > 0 && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<Building2 className="h-4 w-4 text-muted-foreground" />
											<Label className="text-muted-foreground">
												Inherited from Organization (
												{inheritedComplianceChecks.length})
											</Label>
										</div>
										{inheritedComplianceChecks.map((check) => (
											<div
												key={check.id}
												className="flex items-start justify-between p-4 border rounded-lg bg-muted/20"
											>
												<div className="flex items-start gap-3">
													<ClipboardCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2 flex-wrap">
															<span className="font-medium">{check.name}</span>
															<Badge
																variant={check.required ? "default" : "outline"}
															>
																{check.required ? "Required" : "Optional"}
															</Badge>
															{check.sourceOrgName && (
																<Badge variant="secondary">
																	From: {check.sourceOrgName}
																</Badge>
															)}
														</div>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								)}

								{/* Add new compliance check */}
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">
										Add Solicitation-Specific Compliance Check
									</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label>Check Name *</Label>
											<Input
												placeholder="e.g., Security Clearance"
												value={newComplianceName}
												onChange={(e) => setNewComplianceName(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label>Required</Label>
											<div className="flex items-center gap-2 pt-2">
												<Checkbox
													checked={newComplianceRequired}
													onCheckedChange={(checked) =>
														setNewComplianceRequired(checked as boolean)
													}
												/>
												<span className="text-sm">
													Vendors must meet this requirement
												</span>
											</div>
										</div>
									</div>
									<div className="space-y-2">
										<Label>Description</Label>
										<Textarea
											placeholder="Describe what this compliance check verifies..."
											value={newComplianceDescription}
											onChange={(e) =>
												setNewComplianceDescription(e.target.value)
											}
											rows={2}
										/>
									</div>
									<Button onClick={handleAddComplianceCheck}>
										<Plus className="mr-2 h-4 w-4" />
										Add Compliance Check
									</Button>
								</div>

								{/* Solicitation-specific compliance checks list */}
								{solicitationComplianceChecks.length > 0 && (
									<div className="space-y-3 mt-6">
										<Label>
											Solicitation-Specific Checks (
											{solicitationComplianceChecks.length})
										</Label>
										{solicitationComplianceChecks.map((check) => (
											<div
												key={check.id}
												className="flex items-start justify-between p-4 border rounded-lg"
											>
												<div className="flex items-start gap-3">
													<ClipboardCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{check.name}</span>
															<Badge
																variant={check.required ? "default" : "outline"}
															>
																{check.required ? "Required" : "Optional"}
															</Badge>
														</div>
														{check.description && (
															<p className="text-sm text-muted-foreground mt-1">
																{check.description}
															</p>
														)}
													</div>
												</div>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setSolicitationComplianceChecks(
															solicitationComplianceChecks.filter(
																(c) => c.id !== check.id,
															),
														);
														toast.success(isPublished ? "Compliance check will be archived on save" : "Compliance check removed");
													}}
													title={isPublished ? "Archive compliance check" : "Remove compliance check"}
												>
													{isPublished ? (
														<Archive className="h-4 w-4" />
													) : (
														<Trash2 className="h-4 w-4" />
													)}
												</Button>
											</div>
										))}
									</div>
								)}

								{solicitationComplianceChecks.length === 0 &&
									inheritedComplianceChecks.length === 0 && (
										<div className="text-center py-8 text-muted-foreground">
											<ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
											<p>No compliance checks configured.</p>
										</div>
									)}

								{/* Show Archived compliance checks */}
								{archivedComplianceChecks.length > 0 && (
									<Collapsible className="mt-4">
										<CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
											<ChevronDown className="h-4 w-4" />
											Show Archived ({archivedComplianceChecks.length})
										</CollapsibleTrigger>
										<CollapsibleContent className="mt-2 space-y-2 pl-6 border-l-2 border-muted">
											{archivedComplianceChecks.map((check) => (
												<div
													key={check.id}
													className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 opacity-60"
												>
													<div className="flex items-center gap-3">
														<ClipboardCheck className="h-5 w-5 text-muted-foreground" />
														<div>
															<p className="font-medium line-through">{check.title}</p>
															<p className="text-sm text-muted-foreground">
																Archived
																{check.is_required && " • Was required"}
															</p>
														</div>
													</div>
												</div>
											))}
										</CollapsibleContent>
									</Collapsible>
								)}
							</CardContent>
						</Card>

						<PdfFormEditorV2
							open={formEditorOpen}
							onClose={() => {
								setFormEditorOpen(false);
								setEditingForm(null);
								setNewFormName("");
								setNewFormDescription("");
								setNewFormRequired(true);
							}}
							onSave={async (formData) => {
								if (editingForm) {
									await handleUpdateExistingForm({
										...formData,
										pdfStoragePath: formData.pdfStoragePath,
									});
								} else {
									await handleAddNewForm({
										...formData,
										pdfStoragePath: formData.pdfStoragePath,
									});
								}
								setFormEditorOpen(false);
								setEditingForm(null);
								setNewFormName("");
								setNewFormDescription("");
								setNewFormRequired(true);
							}}
							initialPdfStoragePath={editingForm?.pdfStoragePath || null}
							formName={editingForm?.name || newFormName}
							formDescription={editingForm?.description || newFormDescription}
							formTemplateId={editingForm?.formTemplateId}
							organizationId={user?.organizationId || ""}
						/>
					</TabsContent>

					<TabsContent value="evaluation" className="space-y-6">
						{/* Use Organization Criteria Checkbox - only show if org has criteria */}
						{hasOrgCriteria && (
						<div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/30">
							<Checkbox
								id="useOrgCriteria"
								checked={useOrgCriteria}
								onCheckedChange={(checked) => setUseOrgCriteria(checked as boolean)}
							/>
							<div className="grid gap-1.5 leading-none">
								<Label
									htmlFor="useOrgCriteria"
									className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
								>
									Use organization evaluation criteria
								</Label>
								<p className="text-sm text-muted-foreground">
									{useOrgCriteria
										? "Using inherited rubric and weights from the organization. Uncheck to define custom evaluation settings."
										: "Define custom evaluation rubric and weights for this solicitation."}
								</p>
							</div>
						</div>
						)}

						{/* Evaluation Criteria */}
						<Card className={useOrgCriteria && hasOrgCriteria ? "bg-muted/30" : ""}>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Target className="h-5 w-5" />
									Evaluation Rubric
								</CardTitle>
								<CardDescription>
									Define the criteria used to evaluate vendor responses. Total
									points: {totalPoints}
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								{/* Add/Edit Criterion Form - only show when not using org criteria */}
								{(!hasOrgCriteria || !useOrgCriteria) && (
								<div className="border rounded-lg p-4 space-y-4 bg-muted/30">
									<h4 className="font-medium">
										{editingCriterion ? "Edit Criterion" : "Add New Criterion"}
									</h4>
									<div className="grid gap-4 md:grid-cols-2">
										<div className="space-y-2">
											<Label htmlFor="criterionTitle">Title *</Label>
											<Input
												id="criterionTitle"
												placeholder="e.g., Technical Approach"
												value={criterionTitle}
												onChange={(e) => setCriterionTitle(e.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="criterionPoints">Max Points</Label>
											<Input
												id="criterionPoints"
												type="number"
												min={1}
												max={100}
												value={criterionPoints}
												onChange={(e) =>
													setCriterionPoints(Number(e.target.value))
												}
											/>
										</div>
									</div>
									<div className="space-y-2">
										<Label htmlFor="criterionDescription">Description</Label>
										<Textarea
											id="criterionDescription"
											placeholder="Describe what evaluators should look for..."
											value={criterionDescription}
											onChange={(e) => setCriterionDescription(e.target.value)}
										/>
									</div>
									<div className="flex gap-2">
										{editingCriterion ? (
											<>
												<Button onClick={handleUpdateCriterion}>
													<Save className="mr-2 h-4 w-4" />
													Update Criterion
												</Button>
												<Button
													variant="outline"
													onClick={() => {
														setEditingCriterion(null);
														setCriterionTitle("");
														setCriterionDescription("");
														setCriterionPoints(10);
													}}
												>
													Cancel
												</Button>
											</>
										) : (
											<Button onClick={handleAddCriterion}>
												<Plus className="mr-2 h-4 w-4" />
												Add Criterion
											</Button>
										)}
									</div>
								</div>
								)}

								{/* Criteria List */}
								<div className="space-y-3">
									{criteria.map((criterion, index) => {
										// Check if this criterion is inherited (from org criteria)
										const inheritedCriterion = useOrgCriteria
											? orgEvalCriteria?.find((c) => c.id === criterion.id)
											: null;
										const isInherited = inheritedCriterion?.isInherited ?? false;

										return (
										<div
											key={criterion.id}
											className={`border rounded-lg p-4 flex items-start justify-between gap-4 ${
												hasOrgCriteria && useOrgCriteria ? "bg-muted/30" : ""
											}`}
										>
											<div className="flex-1">
												<div className="flex items-center gap-2 flex-wrap">
													<Badge variant="outline">{index + 1}</Badge>
													<h4 className="font-medium">{criterion.title}</h4>
													<Badge>{criterion.maxPoints} pts</Badge>
													{hasOrgCriteria && useOrgCriteria && isInherited && inheritedCriterion && (
														<Badge variant="secondary">
															From: {inheritedCriterion.sourceOrganizationName}
														</Badge>
													)}
													{hasOrgCriteria && useOrgCriteria && !isInherited && (
														<Badge variant="secondary">Organization</Badge>
													)}
												</div>
												{criterion.description && (
													<p className="text-sm text-muted-foreground mt-1">
														{criterion.description}
													</p>
												)}
											</div>
											{(!hasOrgCriteria || !useOrgCriteria) && (
											<div className="flex gap-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => handleEditCriterion(criterion)}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => handleDeleteCriterion(criterion.id)}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
											)}
										</div>
										);
									})}
									{criteria.length === 0 && (
										<div className="text-center py-8 text-muted-foreground">
											{hasOrgCriteria && useOrgCriteria
												? "No organization evaluation criteria found. The organization has not defined any criteria."
												: "No evaluation criteria defined. Add at least one criterion above."}
										</div>
									)}
								</div>
							</CardContent>
						</Card>

						{/* Scoring Weights */}
						{criteria.length > 0 && (
						<Card className={hasOrgCriteria && useOrgCriteria ? "bg-muted/30" : ""}>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Scale className="h-5 w-5" />
									Scoring Weights
								</CardTitle>
								<CardDescription>
									{hasOrgCriteria && useOrgCriteria
										? "Using organization weights. Uncheck above to customize."
										: `Adjust the relative importance of each criterion. Total: ${totalWeight}%`}
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								{criteria.map((criterion) => {
									const sliderValue = criteriaWeights[criterion.id] ?? 0;
									return (
									<div key={criterion.id} className="space-y-2">
										<div className="flex items-center justify-between">
											<Label>{criterion.title}</Label>
											<span className="text-sm font-medium">
												{sliderValue}%
											</span>
										</div>
										<Slider
											value={[sliderValue]}
											onValueChange={([value]) =>
												handleWeightChange(criterion.id, value)
											}
											max={100}
											step={1}
											disabled={hasOrgCriteria && useOrgCriteria}
										/>
									</div>
									);
								})}
								{(!hasOrgCriteria || !useOrgCriteria) && totalWeight !== 100 && (
									<p className="text-sm text-destructive">
										Weights must total 100% to save. Currently at {totalWeight}%.
									</p>
								)}
							</CardContent>
						</Card>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</DashboardLayout>
	);
}
